import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { mdiDelete } from '@mdi/js';
import Icon from '@mdi/react';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/IconButton';
import { Select } from '../Select/Select';
import { relevant } from '../../api';
import { stores } from '../../stores';
import classes from '../../api/classes';

const { SlackIntegration, TriggerInfoChannel } = classes;

interface Channel {
	id: string;
	name: string;
}

interface SlackConnection {
	teamName?: string;
}

interface SlackChannelSelectProps {
	model: {
		isGlobal: boolean;
		slackRecipients: [{
			channelId: string;
			channelName: string;
			addedByUser: string;
		}] | null,
		type: string,
	};
}

export function SlackChannelSelect({ model }: SlackChannelSelectProps) {
	const { isGlobal } = model;
	const userId = isGlobal ? 'slackGlobal' : stores.identity.userId();

	const updateSlackRecipient = (channel: Channel) => {
		model.slackRecipients = [{
			channelId: channel.id,
			channelName: channel.name,
			addedByUser: userId,
		}];
	};

	const [slackEnabled, setSlackEnabled] = useState<boolean>(false);

	useEffect(() => {
		(async () => {
			const enabled = await SlackIntegration.callStatic('isEnabled');
			setSlackEnabled(!!enabled);
		})();
	}, []);

	const [connectedWorkspace, setConnectedWorkspace] = useState<string | null>(null);
	const [channels, setChannels] = useState<Channel[]>([]);

	const initialRecipient = model.slackRecipients?.[0];
	const initialChannel = initialRecipient?.channelId
		? {
			id: initialRecipient.channelId,
			name: initialRecipient.channelName || '(Channel not found)',
		}
		: null;

	const [selectedChannel, setSelectedChannel] = useState<Channel | null>(initialChannel);

	// Shared Slack data fetcher
	const resolveConnection = useCallback(async (): Promise<SlackConnection> => {
		return SlackIntegration.callStatic('resolveConnection', { userId });
	}, [userId]);

	// Update channels
	const refreshSlackState = useCallback(async () => {
		const { teamName = null } = (await resolveConnection()) || {};
		if (!teamName) return;

		setConnectedWorkspace(teamName);

		const fetchedChannels = (await SlackIntegration.callStatic('getChannels', {
			userId,
			withUsers: true,
		})) as Channel[];
		setChannels(fetchedChannels);
	}, [resolveConnection, userId]);

	// Load Slack state on the first mount
	useEffect(() => {
		refreshSlackState();
	}, [refreshSlackState]);

	// postMessage handler
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.data?.type === 'slack-install-complete') {
				refreshSlackState();
			}
		};
		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [refreshSlackState]);

	// Restore the selected channel if already configured
	useEffect(() => {
		const recipient = model.slackRecipients?.[0];
		if (!recipient?.channelId || channels.length === 0) return;

		const match = channels.find((c) => c.id === recipient.channelId);
		if (!match) return;

		if (recipient.channelName !== match.name) {
			recipient.channelName = match.name;
		}
		setSelectedChannel(match);
	}, [channels, model.slackRecipients]);

	const handleInstall = () => window.open(`${relevant.baseUrl}slack/install?user=${userId}`, '_blank');

	const handleSelectChannel = (id: string) => {
		const selected = channels.find((channel) => channel.id === id);
		if (!selected) return;
		setSelectedChannel(selected);
		updateSlackRecipient(selected);
	};

	const handleDelete = () => {
		model.slackRecipients = null;
		setSelectedChannel(null);
	};

	const channelOptions = useMemo(() => {
		return channels.map(({ id, name }) => ({
			value: id,
			label: name,
		}));
	}, [channels]);

	return slackEnabled && (
		<Card sx={{ overflowY: 'visible' }}>
			<CardContent>
				<Typography variant="h3">Slack</Typography>

				{connectedWorkspace && (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant="body1" sx={{ fontWeight: 300 }}>
								Connected to workspace: {connectedWorkspace}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Select
								placeholder={selectedChannel?.name || 'Select a channel'}
								value={selectedChannel?.id}
								onChange={handleSelectChannel}
								options={channelOptions}
								label="Channel"
								fullWidth
								searchable
								inline
							/>
							<Typography variant="caption" sx={{ fontWeight: 300 }}>
								Only the channels the bot is invited to are listed.
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<IconButton icon={<Icon path={mdiDelete} />} onClick={handleDelete} />
						</Grid>
					</Grid>
				)}
				<Button onClick={handleInstall}>
					{connectedWorkspace ? 'Connect to a different workspace' : 'Connect to a Slack workspace'}
				</Button>
			</CardContent>
		</Card>
	);
}
